import { isEqual } from 'es-toolkit';
import { useCallback, useEffect, useRef } from 'react';

import { map } from '@flipster/utils/map';

import { DrawingEventType, EntityId, LineToolState, LineToolsAndGroupsState } from '../../lib/charting_library';
import { useTradingViewDataStorage } from '../misc/data-manager';
import TradingView from '../trading-view';

interface Props {
  tv: TradingView | undefined;
}

function getLineToolsState(tv: TradingView) {
  // https://github.com/tradingview/charting_library/issues/8890#issuecomment-2569245945
  // 모든 라인 툴을 가져오기 위해 두 가지 호출을 결합
  // getLineToolsState(0): 이전 세션에서 불러온 라인 툴
  // getLineToolsState(1): 현재 세션에서 생성한 라인 툴
  // @ts-ignore - 문서화되지 않은 파라미터이지만 실제로 작동함
  const prevSessionTools = tv.activeChart.getLineToolsState(0);
  // @ts-ignore
  const currentSessionTools = tv.activeChart.getLineToolsState(1);
  // 두 라인 툴 상태를 병합
  const combinedTools: LineToolsAndGroupsState = {
    sources: new Map([...(prevSessionTools.sources || []), ...(currentSessionTools.sources || [])]),
    groups: new Map([...(prevSessionTools.groups || []), ...(currentSessionTools.groups || [])]),
  };

  return combinedTools;
}

export const SetupDrawing = ({ tv }: Props) => {
  const chartInitialized = useRef(false);
  const restoring = useRef(false);
  const drawingsChanged = useRef(false);
  const [lineTools, setLineTools] = useTradingViewDataStorage({
    key: 'lineTools',
  });

  const updateLineToolsDrawing = useCallback(async () => {
    if (!tv || tv.symbol === 'dummy') return;
    await tv.ready();
    restoring.current = true;
    const lastLineTools = (await lineTools.asyncValue())[tv.symbol] ?? [];
    const extractedCurrentLineTools = map(getLineToolsState(tv), extractLineTools);
    if (
      !isEqual(lastLineTools, extractedCurrentLineTools) ||
      lastLineTools.length < (tv?.activeChart?.getAllShapes().length ?? 0)
    ) {
      if ((extractedCurrentLineTools?.length ?? 0) > 0) {
        // Cleanup before apply line tools state
        // Without cleanup, `delete` action not restored from previous session
        tv?.activeChart?.getAllShapes()?.forEach((shape) => {
          tv?.activeChart?.removeEntity(shape.id, { disableUndo: true });
        });
      }
      await tv?.activeChart?.applyLineToolsState(expandLineTools(lastLineTools));
    }
    restoring.current = false;
  }, [tv, lineTools]);

  useEffect(() => {
    if (!chartInitialized.current) return;
    updateLineToolsDrawing();
  }, [updateLineToolsDrawing]);

  useEffect(
    () =>
      tv?.on('onFirstDataLoaded', async () => {
        await updateLineToolsDrawing();
        chartInitialized.current = true;
      }),
    [tv, updateLineToolsDrawing],
  );

  useEffect(() => {
    if (!tv) return;

    const markChanged = (sourceId: EntityId, drawingEventType: DrawingEventType) => {
      if (restoring.current) {
        return;
      }
      drawingsChanged.current = true;
      /**
       * NOTE: Very tricky logic for bug of trading view
       * When user creates indicator with text input, `onAutoSaveNeeded` would be fired before complete type the text.
       * But when user complete type the text, just `drawing_event` fired and auto save not works.
       */
      if (drawingEventType === 'properties_changed') {
        const tools = getLineToolsState(tv);
        // oxlint-disable-next-line typescript/no-explicit-any
        const source = tools.sources?.get(sourceId)?.state as any;
        if (typeof source?.state?.text === 'string' && source.state.text.length > 0) {
          callback();
        }
      }
    };
    const callback = () => {
      if (tv.symbol === 'dummy') {
        drawingsChanged.current = false;
      }
      if (!tv.activeChart || !drawingsChanged.current) return;
      const combinedTools = getLineToolsState(tv);
      const transformedLineTools = extractLineTools(combinedTools);
      setLineTools((prev) => ({ ...prev, [tv.symbol]: transformedLineTools }));
    };
    const chart = tv.widget;
    const offIntervalChanged = tv.on('onIntervalChanged', () => (chartInitialized.current = false));
    chart?.subscribe('drawing_event', markChanged);
    chart?.subscribe('onAutoSaveNeeded', callback);

    return () => {
      offIntervalChanged();
      try {
        chart?.unsubscribe('drawing_event', markChanged);
        chart?.unsubscribe('onAutoSaveNeeded', callback);
      } catch {
        // ignore internal error
      }
    };
  }, [tv, setLineTools]);

  return null;
};

interface LineToolsSourcesState {
  id?: string;
  type?: string;
}

function extractLineTools(lineTools: LineToolsAndGroupsState) {
  const lineToolsArray: Array<LineToolState> = [];
  for (const [_key, source] of lineTools.sources ?? []) {
    if (source?.state && (source.state as LineToolsSourcesState).id) lineToolsArray.push(source);
  }
  return lineToolsArray;
}

function expandLineTools(serializedLineTools: Array<LineToolState>): LineToolsAndGroupsState {
  const sources = new Map<EntityId, LineToolState>();
  for (const source of serializedLineTools) {
    if (source?.state && (source.state as LineToolsSourcesState).id) sources.set(source.id, source);
  }
  return { sources, groups: new Map() };
}
